import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarFooter, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { ChevronDown, ChevronRight, Search, PanelLeftClose, PanelLeftOpen, Sun, Moon, Monitor } from "lucide-react";
import { MetalPriceTicker } from "@/components/header/metal-price-ticker";
import { AskAiBubble } from "@/components/ask-ai/ask-ai-bubble";
import { Button } from "@/components/ui/button";
import { HelpCircle, Keyboard, FlaskConical, X, Menu, UserCircle } from "lucide-react";
import { useHelpDrawer } from "@/components/vc/VCHelpDrawer";
import { ShortcutsProvider, useShortcuts } from "@/components/shortcuts/shortcuts-context";
import { useMe, useMyFeatures } from "@/lib/auth";
import { UserMenu } from "@/components/layout/user-menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { NotificationBell } from "@/components/notification-bell";
import { useBranding, resolveLogoSrc } from "@/components/branding-provider";
import { useTheme } from "@/components/theme-provider";
import { METALUI_BRAND } from "@workspace/brand";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  customFetch,
  useListPackingJobs,
  getListPackingJobsQueryKey,
  useGetReturnsOverview,
  getGetReturnsOverviewQueryKey,
  useGetTaskDashboardSummary,
  getGetTaskDashboardSummaryQueryKey,
} from "@workspace/api-client-react";
import { useStatusSummary } from "@/hooks/use-status-summary";
import { getLastUsableContentMark } from "@/lib/app-performance";
import { subscribeSalesLifecycleChanges } from "@/lib/sales-lifecycle-sync";
import { TabsProvider, UnsavedChangesGuard } from "@/components/tabs/tabs-context";
import { TabBar } from "@/components/tabs/tab-bar";
import { MobileBottomNav } from "@/components/layout/mobile-bottom-nav";
import { MobileNavigation, type MobileBadgeCounts } from "@/components/layout/mobile-navigation";
import {
  useSidebarItems,
  useSidebarStyling,
  isExternalHref,
  type SidebarItemNode,
} from "@/lib/sidebar-config";
import { getIcon } from "@/lib/sidebar-icons";
import { SIDEBAR_EMOJI_MAP } from "@/lib/sidebar-emoji-map";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { CommandPaletteProvider, useCommandPalette, VCCommandPalette } from "@/components/vc";
import { useSidebarFavourites } from "@/hooks/use-sidebar-favourites";
import { useRecentPages, type RecentPage } from "@/hooks/use-recent-pages";
import { usePreference } from "@/hooks/use-preference";
import { useJitteredInterval } from "@/lib/jitter";
import { groupSidebarItems } from "@/lib/sidebar-groups";
import { preloadRoute } from "@/lib/app-performance";

export type BadgeCounts = {
  unreadMessages: number;
  pendingSocialApprovals: number;
  unreadSocialInbox: number;
  pendingPacking: number;
  openReturns: number;
  overdueTasks: number;
};

const PRELOADABLE_ROUTE_PATHS = new Set(["/inventory", "/sales"]);

/** Start only the two task-critical route chunks on genuine navigation intent. */
function preloadNavigationIntent(href: string): void {
  if (PRELOADABLE_ROUTE_PATHS.has(href)) void preloadRoute(href);
}

const MOBILE_NAV_CACHE_PREFIX = "metalui:authorized-nav-v1";

function readSelectedTenantForNavCache(): string {
  if (typeof window === "undefined") return "default";
  try {
    return window.localStorage.getItem("vc.selectedTenantId") ?? "default";
  } catch {
    return "default";
  }
}

function readCachedNavigation(userId: string | undefined): SidebarItemNode[] {
  if (!userId || typeof window === "undefined") return [];
  try {
    const key = `${MOBILE_NAV_CACHE_PREFIX}:${userId}:${readSelectedTenantForNavCache()}`;
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? "null") as { items?: SidebarItemNode[] } | null;
    return Array.isArray(parsed?.items) ? parsed.items : [];
  } catch {
    return [];
  }
}

function cacheNavigation(userId: string | undefined, items: SidebarItemNode[]) {
  if (!userId || typeof window === "undefined") return;
  try {
    const key = `${MOBILE_NAV_CACHE_PREFIX}:${userId}:${readSelectedTenantForNavCache()}`;
    window.localStorage.setItem(key, JSON.stringify({ items, savedAt: Date.now() }));
  } catch {
    // Navigation must remain usable if local storage is unavailable or full.
  }
}

/**
 * Convert a `#RRGGBB` (or `#RGB`) hex string to the "H S% L%" component
 * triple expected by the theme variables (which are wrapped in `hsl(...)`
 * by consumer CSS). Returns null for invalid input so the caller falls
 * back to the brand defaults instead of injecting a broken value.
 */
function hexToHslComponents(hex: string | null): string | null {
  if (!hex || typeof hex !== "string") return null;
  const m3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hex.trim());
  const m6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  let r: number, g: number, b: number;
  if (m6) {
    r = parseInt(m6[1]!, 16);
    g = parseInt(m6[2]!, 16);
    b = parseInt(m6[3]!, 16);
  } else if (m3) {
    r = parseInt(m3[1]! + m3[1]!, 16);
    g = parseInt(m3[2]! + m3[2]!, 16);
    b = parseInt(m3[3]! + m3[3]!, 16);
  } else {
    return null;
  }
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rN: h = (gN - bN) / d + (gN < bN ? 6 : 0); break;
      case gN: h = (bN - rN) / d + 2; break;
      default: h = (rN - gN) / d + 4;
    }
    h *= 60;
  }
  const round = (n: number, dp = 1): string => {
    const f = Math.pow(10, dp);
    return String(Math.round(n * f) / f);
  };
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

function resolveIconColor(item: SidebarItemNode, fallback: string | null): string | undefined {
  if (item.color) return item.color;
  if (item.accent === "gold") return "hsl(var(--secondary))";
  if (item.accent === "green") return "#059669";
  return fallback ?? undefined;
}

/** Emoji for the item, looked up by builtInId then href fallback. */
function getEmoji(item: SidebarItemNode): string | undefined {
  if (item.builtInId) return SIDEBAR_EMOJI_MAP[item.builtInId];
  return SIDEBAR_EMOJI_MAP[item.href];
}

/**
 * Emoji span shown in expanded sidebar — hidden automatically when the
 * sidebar collapses to icon-only mode via the group data attribute.
 */
function SidebarEmoji({ emoji, collapsed }: { emoji: string | undefined; collapsed: boolean }) {
  if (!emoji) return null;
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-base leading-none select-none" aria-hidden>
            {emoji}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{emoji}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <span
      className="text-sm leading-none select-none flex-shrink-0 w-4 text-center"
      aria-hidden
    >
      {emoji}
    </span>
  );
}

function NavLeaf({
  item,
  isActive,
  badge,
  fallbackIconColor,
}: {
  item: SidebarItemNode;
  isActive: boolean;
  badge: number;
  fallbackIconColor: string | null;
}) {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const Icon = getIcon(item.icon);
  const iconColor = resolveIconColor(item, fallbackIconColor);
  const accentClasses =
    item.accent === "green"
      ? `text-emerald-700 hover:text-emerald-800 ${isActive ? "bg-emerald-50 text-emerald-800" : ""}`
      : item.accent === "gold"
      ? `${isActive ? "bg-[hsl(var(--secondary))]/10" : ""}`
      : "";
  const external = isExternalHref(item.href) || item.openInNewWindow;
  const testId = `nav-${item.id}`;
  const inner = (
    <>
      <Icon className="h-[18px] w-[18px]" style={iconColor ? { color: iconColor } : undefined} />
      {!collapsed && <span className="flex-1 truncate">{item.name}</span>}
      {!collapsed && badge > 0 ? (
        <span
          className="ml-auto inline-flex items-center justify-center rounded-full bg-[hsl(var(--secondary))] text-[10px] font-bold text-black min-w-[1.25rem] h-5 px-1.5"
          data-testid={`badge-nav-${item.id}`}
          aria-label={`${badge} notifications`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      ) : collapsed && badge > 0 ? (
        <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[var(--sidebar-gold)]" aria-label={`${badge} notifications`} />
      ) : null}
    </>
  );
  return (
    <SidebarMenuButton asChild isActive={isActive} className={accentClasses} data-testid={testId} tooltip={`${item.name}${badge > 0 ? `, ${badge} notifications` : ""}`} aria-current={isActive ? "page" : undefined}>
      {external ? (
        <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => isMobile && setOpenMobile(false)}>
          {inner}
        </a>
      ) : (
        <Link
          href={item.href}
          onMouseEnter={() => preloadNavigationIntent(item.href)}
          onFocus={() => preloadNavigationIntent(item.href)}
          onTouchStart={() => preloadNavigationIntent(item.href)}
          onClick={() => isMobile && setOpenMobile(false)}
        >
          {inner}
        </Link>
      )}
    </SidebarMenuButton>
  );
}

function SubNavLeaf({
  item,
  isActive,
  fallbackIconColor,
  favourites,
  badgeCounts,
}: {
  item: SidebarItemNode;
  isActive: boolean;
  fallbackIconColor: string | null;
  favourites: FavouritesShape;
  badgeCounts: BadgeCounts;
}) {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const Icon = getIcon(item.icon);
  const iconColor = resolveIconColor(item, fallbackIconColor);
  const external = isExternalHref(item.href) || item.openInNewWindow;
  const isFaved = favourites.isFavourited(item.id);
  const badge = resolveBadge(item, badgeCounts);
  const inner = (
    <>
      <Icon className="h-[18px] w-[18px]" style={iconColor ? { color: iconColor } : undefined} />
      {!collapsed && <span className="flex-1 truncate">{item.name}</span>}
      {!collapsed && badge > 0 && (
        <span className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--sidebar-gold)] px-1.5 text-[10px] font-bold text-black" aria-label={`${badge} notifications`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </>
  );
  return (
    <SidebarMenuItem>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <SidebarMenuButton
            asChild
            isActive={isActive}
            size="sm"
            className="pl-8"
            data-testid={`nav-${item.id}`}
            tooltip={item.name}
            aria-current={isActive ? "page" : undefined}
          >
            {external ? (
              <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => isMobile && setOpenMobile(false)}>
                {inner}
              </a>
            ) : (
              <Link
                href={item.href}
                onMouseEnter={() => preloadNavigationIntent(item.href)}
                onFocus={() => preloadNavigationIntent(item.href)}
                onTouchStart={() => preloadNavigationIntent(item.href)}
                onClick={() => isMobile && setOpenMobile(false)}
              >
                {inner}
              </Link>
            )}
          </SidebarMenuButton>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={() => (isFaved ? favourites.unpin(item.id) : favourites.pin(item.id))}>
            {isFaved ? "⭐ Remove from Favourites" : "☆ Pin to Favourites"}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </SidebarMenuItem>
  );
}

function resolveBadge(item: SidebarItemNode, badgeCounts: BadgeCounts): number {
  switch (item.badgeKey) {
    case "unreadMessages": return badgeCounts.unreadMessages;
    case "pendingSocialApprovals": return badgeCounts.pendingSocialApprovals;
    case "unreadSocialInbox": return badgeCounts.unreadSocialInbox;
    case "pendingPacking": return badgeCounts.pendingPacking;
    case "openReturns": return badgeCounts.openReturns;
    case "overdueTasks": return badgeCounts.overdueTasks;
    default: return 0;
  }
}

type FavouritesShape = {
  pin: (id: string) => void;
  unpin: (id: string) => void;
  isFavourited: (id: string) => boolean;
};

function SidebarTreeItem({
  item,
  location,
  badgeCounts,
  fallbackIconColor,
  favourites,
}: {
  item: SidebarItemNode;
  location: string;
  badgeCounts: BadgeCounts;
  fallbackIconColor: string | null;
  favourites: FavouritesShape;
}) {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const childActive = item.children.some(
    (c) => location === c.href || location.startsWith(c.href + "/"),
  );
  const selfActive = location === item.href;
  const [open, setOpen] = useState(childActive || selfActive);
  useEffect(() => {
    if (childActive || selfActive) setOpen(true);
  }, [childActive, selfActive]);
  const hasChildren = item.children.length > 0;
  const directBadge = resolveBadge(item, badgeCounts);
  const collapsedBadge = directBadge
    + item.children.reduce((total, child) => total + resolveBadge(child, badgeCounts), 0);
  const isFaved = favourites.isFavourited(item.id);

  const contextContent = (
    <ContextMenuContent>
      <ContextMenuItem onClick={() => (isFaved ? favourites.unpin(item.id) : favourites.pin(item.id))}>
        {isFaved ? "⭐ Remove from Favourites" : "☆ Pin to Favourites"}
      </ContextMenuItem>
    </ContextMenuContent>
  );

  if (!hasChildren) {
    return (
      <SidebarMenuItem>
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <NavLeaf item={item} isActive={selfActive} badge={directBadge} fallbackIconColor={fallbackIconColor} />
          </ContextMenuTrigger>
          {contextContent}
        </ContextMenu>
      </SidebarMenuItem>
    );
  }

  const Icon = getIcon(item.icon);
  const iconColor = resolveIconColor(item, fallbackIconColor);
  const external = isExternalHref(item.href) || item.openInNewWindow;
  const accentClasses =
    item.accent === "green"
      ? `text-emerald-700 hover:text-emerald-800 ${selfActive ? "bg-emerald-50 text-emerald-800" : ""}`
      : item.accent === "gold"
      ? `${selfActive ? "bg-[hsl(var(--secondary))]/10" : ""}`
      : "";

  return (
    <>
      <SidebarMenuItem>
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="flex items-center w-full">
              <SidebarMenuButton
                asChild
                isActive={selfActive}
                className={`${accentClasses} flex-1`}
                data-testid={`nav-${item.id}`}
                tooltip={item.name}
                aria-current={selfActive ? "page" : undefined}
              >
                {external ? (
                  <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => isMobile && setOpenMobile(false)}>
                    <Icon className="h-[18px] w-[18px]" style={iconColor ? { color: iconColor } : undefined} />
                    {!collapsed && <span className="flex-1 truncate">{item.name}</span>}
                    {!collapsed && directBadge > 0 && <span className="ml-auto rounded-full bg-[var(--sidebar-gold)] px-1.5 py-0.5 text-[10px] font-bold text-black" aria-label={`${directBadge} notifications`}>{directBadge > 99 ? "99+" : directBadge}</span>}
                    {collapsed && collapsedBadge > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[var(--sidebar-gold)]" aria-label={`${collapsedBadge} notifications`} />}
                  </a>
                ) : (
                  <Link
                    href={item.href}
                    onMouseEnter={() => preloadNavigationIntent(item.href)}
                    onFocus={() => preloadNavigationIntent(item.href)}
                    onTouchStart={() => preloadNavigationIntent(item.href)}
                    onClick={() => isMobile && setOpenMobile(false)}
                  >
                    <Icon className="h-[18px] w-[18px]" style={iconColor ? { color: iconColor } : undefined} />
                    {!collapsed && <span className="flex-1 truncate">{item.name}</span>}
                    {!collapsed && directBadge > 0 && <span className="ml-auto rounded-full bg-[var(--sidebar-gold)] px-1.5 py-0.5 text-[10px] font-bold text-black" aria-label={`${directBadge} notifications`}>{directBadge > 99 ? "99+" : directBadge}</span>}
                    {collapsed && collapsedBadge > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[var(--sidebar-gold)]" aria-label={`${collapsedBadge} notifications`} />}
                  </Link>
                )}
              </SidebarMenuButton>
              {!collapsed && (
                <button
                  type="button"
                  aria-label={open ? `Collapse ${item.name}` : `Expand ${item.name}`}
                  aria-expanded={open}
                  aria-controls={`nav-children-${item.id}`}
                  className="ml-1 rounded-md p-1 text-muted-foreground hover:bg-sidebar-accent transition-colors"
                  onClick={() => setOpen((v) => !v)}
                  data-testid={`nav-toggle-${item.id}`}
                >
                  {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              )}
            </div>
          </ContextMenuTrigger>
          {contextContent}
        </ContextMenu>
      </SidebarMenuItem>
      {!collapsed ? (
        <li id={`nav-children-${item.id}`} className={open ? "contents" : "hidden"}>
        <ul className="contents">
        {item.children.map((child) => {
            const isActive = location === child.href || location.startsWith(child.href + "/");
            return (
              <SubNavLeaf
                key={child.id}
                item={child}
                isActive={isActive}
                fallbackIconColor={fallbackIconColor}
                favourites={favourites}
                badgeCounts={badgeCounts}
              />
            );
          })}
        </ul>
        </li>
      ) : null}
    </>
  );
}

function FavouritesSection({
  navItems,
  location,
  badgeCounts,
  fallbackIconColor,
  favouriteIds,
  favourites,
}: {
  navItems: SidebarItemNode[];
  location: string;
  badgeCounts: BadgeCounts;
  fallbackIconColor: string | null;
  favouriteIds: string[];
  favourites: FavouritesShape;
}) {
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const [open, setOpen] = useState(true);

  const flat = navItems.flatMap((n) => [n, ...n.children]);
  const pinnedItems = favouriteIds
    .map((id) => flat.find((n) => n.id === id))
    .filter((n): n is SidebarItemNode => !!n);

  if (pinnedItems.length === 0) return null;
  if (collapsed) return null;

  return (
    <SidebarGroup className="py-0">
      <div className="flex items-center justify-between px-2 pt-2 pb-0.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-foreground/50 select-none">
          ⭐ Favourites
        </span>
        <button
          type="button"
          className="text-sidebar-foreground/40 hover:text-sidebar-foreground transition-colors p-0.5 rounded"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Collapse favourites" : "Expand favourites"}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
      </div>
      {open && (
        <SidebarGroupContent>
          <SidebarMenu>
            {pinnedItems.map((item) => {
              const isActive = location === item.href || location.startsWith(item.href + "/");
              const badge = resolveBadge(item, badgeCounts);
              return (
                <SidebarMenuItem key={item.id}>
                  <ContextMenu>
                    <ContextMenuTrigger asChild>
                      <NavLeaf
                        item={item}
                        isActive={isActive}
                        badge={badge}
                        fallbackIconColor={fallbackIconColor}
                      />
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      <ContextMenuItem onClick={() => favourites.unpin(item.id)}>
                        ⭐ Remove from Favourites
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}

function RecentSection({
  recentPages,
  location,
}: {
  recentPages: RecentPage[];
  location: string;
}) {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const [open, setOpen] = usePreference("sidebar.recentSectionOpen", true);

  if (recentPages.length === 0 || collapsed) return null;

  return (
    <SidebarGroup className="py-0">
      <div className="flex items-center justify-between px-2 pt-2 pb-0.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-foreground/50 select-none">
          🕘 Recent
        </span>
        <button
          type="button"
          className="text-sidebar-foreground/40 hover:text-sidebar-foreground transition-colors p-0.5 rounded"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Collapse recent pages" : "Expand recent pages"}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
      </div>
      {open && (
        <SidebarGroupContent>
          <SidebarMenu>
            {recentPages.map((page) => {
              const isActive = location === page.href;
              return (
                <SidebarMenuItem key={page.href}>
                  <SidebarMenuButton asChild isActive={isActive} size="sm">
                    <Link
                      href={page.href}
                      onMouseEnter={() => preloadNavigationIntent(page.href)}
                      onFocus={() => preloadNavigationIntent(page.href)}
                      onTouchStart={() => preloadNavigationIntent(page.href)}
                      onClick={() => isMobile && setOpenMobile(false)}
                    >
                      {page.emoji && (
                        <span className="text-sm leading-none select-none flex-shrink-0 w-4 text-center" aria-hidden>
                          {page.emoji}
                        </span>
                      )}
                      <span className="flex-1 truncate">{page.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}

function PaletteTriggerButton({ sidebar = false }: { sidebar?: boolean }) {
  const { openPalette } = useCommandPalette();
  const isMac = typeof navigator !== "undefined" && navigator.platform?.includes("Mac");
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={sidebar
        ? "flex h-10 w-full items-center justify-start gap-3 rounded-[10px] border border-sidebar-border bg-sidebar-accent/40 px-3 text-sidebar-foreground/65 hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
        : "hidden sm:flex items-center gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground"}
      onClick={openPalette}
      aria-label="Open command palette"
      data-testid="nav-command-palette"
    >
      <Search className="h-4 w-4" />
      {sidebar ? (
        <>
          <span className="flex-1 text-left text-sm group-data-[collapsible=icon]:hidden">Search</span>
          <kbd className="rounded border border-sidebar-border bg-sidebar px-1.5 py-0.5 text-[10px] group-data-[collapsible=icon]:hidden">{isMac ? "⌘ K" : "Ctrl K"}</kbd>
        </>
      ) : <span className="text-xs">{isMac ? "⌘K" : "Ctrl+K"}</span>}
    </Button>
  );
}

function SidebarFooterControls() {
  const { state, toggleDesktopSidebar } = useSidebar();
  const { theme, setTheme } = useTheme();
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;
  const nextTheme = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
  return (
    <>
      <div className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
        <Button type="button" variant="ghost" size="sm" className="h-9 flex-1 justify-start gap-2 px-2 text-sidebar-foreground/70 group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0" onClick={() => setTheme(nextTheme)} aria-label={`Theme: ${theme}. Change to ${nextTheme}`} title={`Theme: ${theme}`}>
          <ThemeIcon className="h-[18px] w-[18px]" />
          <span className="capitalize group-data-[collapsible=icon]:hidden">{theme} mode</span>
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-sidebar-foreground/70" onClick={toggleDesktopSidebar} aria-label={state === "collapsed" ? "Expand sidebar" : "Collapse sidebar"} title={state === "collapsed" ? "Expand sidebar" : "Collapse sidebar"}>
          {state === "collapsed" ? <PanelLeftOpen className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
        </Button>
      </div>
      <div className="border-t border-sidebar-border pt-1"><UserMenu /></div>
    </>
  );
}

function MobileSidebarClose() {
  const { isMobile, setOpenMobile } = useSidebar();
  if (!isMobile) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="absolute right-2 top-2 h-11 w-11 text-sidebar-foreground"
      onClick={() => setOpenMobile(false)}
      aria-label="Close navigation"
    >
      <X className="h-5 w-5" />
    </Button>
  );
}

export function MainLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const queryClient = useQueryClient();
  const routePath = location.split(/[?#]/, 1)[0] || "/";
  useEffect(() => subscribeSalesLifecycleChanges(() => {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith("/api/sales"),
    });
  }), [queryClient]);
  const [inventoryReadiness, setInventoryReadiness] = useState(() => ({
    routePath,
    ready: routePath !== "/inventory",
  }));
  useEffect(() => {
    if (routePath !== "/inventory") {
      setInventoryReadiness({ routePath, ready: true });
      return;
    }

    setInventoryReadiness({ routePath, ready: false });
    const onRouteNavigation = (event: Event) => {
      const targetRoute = (event as CustomEvent<{ route?: string }>).detail?.route;
      setInventoryReadiness({
        routePath: targetRoute ?? "",
        ready: targetRoute !== "/inventory",
      });
    };
    const onUsableContent = (event: Event) => {
      const mark = (event as CustomEvent<{
        route?: string;
        reason?: string;
      }>).detail;
      if (mark?.route === "/inventory" && mark.reason === "inventory-primary-list") {
        setInventoryReadiness({ routePath: "/inventory", ready: true });
      }
    };

    window.addEventListener("app:route-navigation", onRouteNavigation);
    window.addEventListener("app:usable-content", onUsableContent);

    // A cached Inventory page can mark itself ready before this effect
    // subscribes. The performance module keeps the latest mark for this case.
    const lastMark = getLastUsableContentMark();
    if (lastMark?.route === "/inventory" && lastMark.reason === "inventory-primary-list") {
      setInventoryReadiness({ routePath: "/inventory", ready: true });
    }

    return () => {
      window.removeEventListener("app:route-navigation", onRouteNavigation);
      window.removeEventListener("app:usable-content", onUsableContent);
    };
  }, [routePath]);
  const shellReadsReady =
    routePath !== "/inventory" ||
    (inventoryReadiness.routePath === routePath && inventoryReadiness.ready);
  const { openHelp } = useHelpDrawer();
  const { has, isLoading, me } = useMe();
  const { hasFeature, isDemo } = useMyFeatures();
  const showMarketTicker =
    !isLoading && has("markets.view") && hasFeature("market_tools_enabled");
  const showDeferredMarketTicker = showMarketTicker && shellReadsReady;
  const branding = useBranding();
  const { resolvedTheme } = useTheme();
  const itemsQ = useSidebarItems(shellReadsReady);
  const stylingQ = useSidebarStyling(shellReadsReady);
  const cachedNavItems = useMemo(() => readCachedNavigation(me?.id), [me?.id]);
  const serverNavItems = itemsQ.data?.items;
  const navItems = serverNavItems ?? cachedNavItems;
  const sidebarReady = !isLoading && (serverNavItems !== undefined || cachedNavItems.length > 0);
  const groupedNavItems = groupSidebarItems(navItems);
  const sidebarErrored = !itemsQ.isLoading && !itemsQ.data && !!itemsQ.error;

  // ── Unified badge polling (Task #1958) ─────────────────────────────────────
  // One endpoint coalesces all badge counts. When `unified_badge_poll_disabled`
  // is set for this tenant the hook returns `isDisabled: true` and the
  // individual per-badge polls below take over as a fallback.
  const summary = useStatusSummary(/* enablePolling = */ true, shellReadsReady);
  const useIndividualPolls = summary.isDisabled;

  // Stable per-mount jittered intervals used ONLY in individual-poll fallback
  // mode. Always constructed (hooks must be unconditional) but only active
  // when `useIndividualPolls` is true.
  const approvalsInterval = useJitteredInterval(60_000);
  const inboxInterval = useJitteredInterval(60_000);
  const packingInterval = useJitteredInterval(120_000);
  const returnsInterval = useJitteredInterval(120_000);
  const tasksInterval = useJitteredInterval(120_000);

  // ── Individual fallback polls — only active when unified polling is off ─────
  const canApprovePost = !isLoading && has("social.posts.approve");
  const { data: pendingApprovalsData } = useQuery<{ count: number }>({
    queryKey: ["/api/social/approvals/pending-count"],
    queryFn: () => customFetch<{ count: number }>("/api/social/approvals/pending-count"),
    enabled: shellReadsReady && useIndividualPolls && canApprovePost,
    refetchInterval: approvalsInterval,
    staleTime: 30_000,
  });

  const canViewSocialInbox = !isLoading && has("social.inbox.view");
  const { data: unreadInboxData } = useQuery<{ count: number }>({
    queryKey: ["/api/social/inbox/unread-count"],
    queryFn: () => customFetch<{ count: number }>("/api/social/inbox/unread-count"),
    enabled: shellReadsReady && useIndividualPolls && canViewSocialInbox,
    refetchInterval: inboxInterval,
    staleTime: 30_000,
  });

  const canViewPacking = !isLoading && has("view_packing");
  const { data: packingData } = useListPackingJobs(
    { status: "pending,in_progress" },
    {
      query: {
        queryKey: getListPackingJobsQueryKey({ status: "pending,in_progress" }),
        enabled: shellReadsReady && useIndividualPolls && canViewPacking,
        refetchInterval: packingInterval,
        staleTime: 60_000,
      },
    },
  );

  const canViewReturns = !isLoading && hasFeature("returns_module_enabled") && has("returns.view");
  const { data: returnsData } = useGetReturnsOverview({
    query: {
      queryKey: getGetReturnsOverviewQueryKey(),
      enabled: shellReadsReady && useIndividualPolls && canViewReturns,
      refetchInterval: returnsInterval,
      staleTime: 60_000,
    },
  });

  const canViewTasks = !isLoading && hasFeature("tasks_module_enabled") && has("tasks.view");
  const { data: taskSummaryData } = useGetTaskDashboardSummary({
    query: {
      queryKey: getGetTaskDashboardSummaryQueryKey(),
      enabled: shellReadsReady && useIndividualPolls && canViewTasks,
      refetchInterval: tasksInterval,
      staleTime: 60_000,
    },
  });

  // ── Badge counts — resolved from unified summary or individual fallbacks ────
  const badgeCounts: BadgeCounts = {
    // Message counts are part of the shared, permission-aware status summary.
    // Do not re-run the marketplace or TikTok dashboard summaries from the
    // shell just to render this badge.
    unreadMessages: summary.unreadMessages ?? 0,
    pendingSocialApprovals: useIndividualPolls
      ? (pendingApprovalsData?.count ?? 0)
      : (summary.pendingSocialApprovals ?? 0),
    unreadSocialInbox: useIndividualPolls
      ? (unreadInboxData?.count ?? 0)
      : (summary.unreadSocialInbox ?? 0),
    pendingPacking: useIndividualPolls
      ? (packingData?.items?.length ?? 0)
      : (summary.pendingPacking ?? 0),
    openReturns: useIndividualPolls
      ? (returnsData?.open ?? 0)
      : (summary.openReturns ?? 0),
    overdueTasks: useIndividualPolls
      ? (taskSummaryData?.myOverdueTasks?.length ?? 0)
      : (summary.overdueTasks ?? 0),
  };

  const styling = stylingQ.data?.styling;
  const cssVars: React.CSSProperties = {};
  const activeHsl = hexToHslComponents(styling?.activeColor ?? null);
  const hoverHsl = hexToHslComponents(styling?.hoverColor ?? null);
  if (activeHsl) {
    (cssVars as Record<string, string>)["--sidebar-primary"] = activeHsl;
    (cssVars as Record<string, string>)["--sidebar-ring"] = activeHsl;
  }
  if (hoverHsl) {
    (cssVars as Record<string, string>)["--sidebar-accent"] = hoverHsl;
  }
  const fallbackIconColor = styling?.iconColor ?? null;

  const { favouriteIds, pin, unpin, isFavourited } = useSidebarFavourites();
  const { recentPages, recordPage } = useRecentPages();

  const favourites: FavouritesShape = { pin, unpin, isFavourited };

  useEffect(() => {
    if (serverNavItems !== undefined) cacheNavigation(me?.id, serverNavItems);
  }, [me?.id, serverNavItems]);

  useEffect(() => {
    if (!sidebarReady || navItems.length === 0) return;
    const flat = navItems.flatMap((n) => [n, ...n.children]);
    const match = flat.find(
      (n) => n.href === location || (n.href !== "/" && location.startsWith(n.href + "/")),
    );
    const label = match?.name ?? location;
    const emoji = match
      ? (match.builtInId ? SIDEBAR_EMOJI_MAP[match.builtInId] : SIDEBAR_EMOJI_MAP[match.href])
      : undefined;
    if (location !== "/" && label && label !== location) {
      recordPage(location, label, emoji);
    }
  }, [location, sidebarReady]);

  const [sidebarCollapsed, setSidebarCollapsed] = usePreference("sidebarCollapsed", false);

  return (
    <CommandPaletteProvider>
    <SidebarProvider className="sidebar-redesign" open={!sidebarCollapsed} onOpenChange={(v) => setSidebarCollapsed(!v)} style={cssVars}>
    <TabsProvider>
      <ShortcutsProvider>
      <UnsavedChangesGuard />
      <TooltipProvider>
      <div className="flex h-screen min-h-screen w-full overflow-hidden bg-background">
        <Sidebar
          collapsible="icon"
          mobileContent={
            <MobileNavigation
              navItems={navItems}
              recentPages={recentPages}
              favouriteIds={favouriteIds}
              badgeCounts={badgeCounts as MobileBadgeCounts}
              fallbackIconColor={fallbackIconColor}
              onPin={pin}
              onUnpin={unpin}
              isReady={sidebarReady}
              hasError={sidebarErrored}
            />
          }
        >
          {/* Sidebar header — logo + wordmark */}
          <SidebarHeader className="border-b border-sidebar-border p-3 bg-sidebar">
            <div className="flex items-center gap-3 font-bold text-lg text-sidebar-foreground">
              <img
                src={
                  resolveLogoSrc(branding.logoUrl) ||
                  `${import.meta.env.BASE_URL}${
                    resolvedTheme === "dark"
                      ? METALUI_BRAND.shieldFile
                      : METALUI_BRAND.shieldFileBlack
                  }`
                }
                alt={branding.appName}
                className="h-10 w-10 object-contain flex-shrink-0 group-data-[collapsible=icon]:mx-auto"
              />
              <div className="flex flex-col leading-tight min-w-0 group-data-[collapsible=icon]:hidden">
                <span
                  className="tracking-tight font-serif text-sidebar-primary truncate"
                  style={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    ...(branding.appNameColor ? { color: branding.appNameColor } : {}),
                  }}
                >
                  {branding.appName}
                </span>
                <span
                  className="sidebar-dashboard-label text-[13px] leading-none tracking-[0.20em] uppercase text-sidebar-foreground/50"
                >
                  DASHBOARD
                </span>
              </div>
            </div>
            <PaletteTriggerButton sidebar />
            <MobileSidebarClose />
          </SidebarHeader>

          {/* Sidebar nav */}
          <SidebarContent className="bg-sidebar">
            {/* Favourites section */}
            <FavouritesSection
              navItems={navItems}
              location={location}
              badgeCounts={badgeCounts}
              fallbackIconColor={fallbackIconColor}
              favouriteIds={favouriteIds}
              favourites={favourites}
            />

            {/* Recent pages section */}
            <RecentSection recentPages={recentPages} location={location} />

            {!sidebarReady ? (
              <SidebarGroup><div className="px-3 py-2 text-xs text-sidebar-foreground/60">Loading navigation…</div></SidebarGroup>
            ) : sidebarErrored ? (
              <SidebarGroup><div className="px-3 py-2 text-xs text-destructive">Couldn't load navigation. Refresh the page.</div></SidebarGroup>
            ) : navItems.length === 0 ? (
              <SidebarGroup><div className="px-3 py-2 text-xs text-sidebar-foreground/60">No navigation items available for your role.</div></SidebarGroup>
            ) : groupedNavItems.map((group, index) => (
              <SidebarGroup key={group.key} className={`px-2 py-2 ${index > 0 ? "border-t border-sidebar-border" : ""}`}>
                <div className="h-6 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-sidebar-foreground/55 group-data-[collapsible=icon]:sr-only">{group.label}</div>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => (
                      <SidebarTreeItem key={item.id} item={item} location={location} badgeCounts={badgeCounts} fallbackIconColor={fallbackIconColor} favourites={favourites} />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>

          {/* Sidebar footer — user menu */}
          <SidebarFooter className="border-t border-sidebar-border p-2 bg-sidebar">
            <SidebarFooterControls />
          </SidebarFooter>
        </Sidebar>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Sticky header with glass effect */}
          <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border/50">
            <header className="flex min-h-14 flex-wrap items-center gap-3 px-3 md:h-14 md:flex-nowrap md:px-4">
              <SidebarTrigger className="hidden flex-shrink-0 min-[1025px]:flex" data-testid="desktop-sidebar-trigger" />
              <SidebarTrigger
                target="mobile"
                className="flex h-10 w-10 shrink-0 min-[1025px]:hidden"
                aria-label="Open more navigation"
                data-testid="mobile-top-menu"
              />
              <Link
                href="/"
                className="flex min-w-0 items-center gap-2 min-[1025px]:hidden"
                aria-label={`${branding.appName} home`}
                data-testid="mobile-top-brand"
              >
                <img
                  src={
                    resolveLogoSrc(branding.logoUrl) ||
                    `${import.meta.env.BASE_URL}${
                      resolvedTheme === "dark"
                        ? METALUI_BRAND.shieldFile
                        : METALUI_BRAND.shieldFileBlack
                    }`
                  }
                  alt=""
                  className="h-8 w-8 shrink-0 object-contain"
                />
                <span className="max-w-[7rem] truncate font-serif text-sm font-semibold tracking-tight" style={branding.appNameColor ? { color: branding.appNameColor } : undefined}>
                  {branding.appName}
                </span>
              </Link>
              <div className="w-px h-5 bg-border/60 flex-shrink-0 hidden sm:block" />
              {/* Live gold/silver per-oz ticker */}
              {showDeferredMarketTicker ? (
                <div className="hidden min-w-0 flex-1 min-[1025px]:block">
                  <MetalPriceTicker />
                </div>
              ) : null}
              <div className="ml-auto hidden items-center gap-0.5 min-[1025px]:flex">
                <ShortcutsHelpButton />
                <NotificationBell />
                <HelpCentreButton />
                <ThemeToggle />
              </div>
              <div className="ml-auto flex items-center gap-0.5 min-[1025px]:hidden">
                <MobileSearchButton />
                <NotificationBell />
                <button
                  type="button"
                  onClick={() => openHelp("general")}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-[374px]:hidden"
                  aria-label="Help Centre"
                  data-testid="mobile-top-help"
                >
                  <HelpCircle className="h-5 w-5" aria-hidden />
                </button>
                {has("tenant_account.view") ? (
                  <Link
                    href="/account-portal"
                    className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Profile and account"
                    data-testid="mobile-top-profile"
                  >
                    <UserCircle className="h-5 w-5" aria-hidden />
                  </Link>
                ) : null}
              </div>
            </header>
            {showDeferredMarketTicker ? (
              <div className="border-t border-border/40 px-3 py-1.5 min-[1025px]:hidden">
                <MetalPriceTicker />
              </div>
            ) : null}
            <AskAiBubble />
            <TabBar />
          </div>

          {/* Page content area */}
          <div
            className="min-h-0 flex-1 overflow-auto overflow-x-hidden p-3 pb-[var(--mobile-bottom-clearance)] md:pb-6 min-[1025px]:p-6"
            data-testid="page-content-scroll"
            data-virtual-scrollport
          >
            {isDemo && <DemoModeBanner />}
            {children}
          </div>
        </main>

        <MobileBottomNav />

        {/* Command Palette — mounted inside SidebarProvider so useSidebar() works correctly */}
        <VCCommandPalette navItems={navItems} />
      </div>
      </TooltipProvider>
      </ShortcutsProvider>
    </TabsProvider>
    </SidebarProvider>
    </CommandPaletteProvider>
  );
}

function MobileSearchButton() {
  const { openPalette } = useCommandPalette();
  return (
    <button
      type="button"
      onClick={openPalette}
      className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label="Search"
      data-testid="mobile-top-search"
    >
      <Search className="h-5 w-5" aria-hidden />
    </button>
  );
}

function ShortcutsHelpButton() {
  const { toggle, hasPageShortcuts } = useShortcuts();
  const title = hasPageShortcuts
    ? "Keyboard shortcuts for this page (press ?)"
    : "Keyboard shortcuts (press ?)";
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Keyboard shortcuts"
      title={title}
      data-testid="nav-shortcuts-help"
      onClick={toggle}
    >
      <Keyboard className="h-5 w-5" />
    </Button>
  );
}

function HelpCentreButton() {
  const { openHelp } = useHelpDrawer();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Help Centre"
      title="Help Centre"
      data-testid="nav-help"
      onClick={() => openHelp("general")}
    >
      <HelpCircle className="h-5 w-5" />
    </Button>
  );
}

/**
 * Task #1238 — Demo Mode banner. Rendered at the top of the page content
 * area whenever the caller's tenant is a demo tenant. Demo tenants never
 * trigger real external side-effects (email, WhatsApp/SMS, marketplaces,
 * postage, QuickBooks, PayPal); this banner makes that state obvious.
 *
 * Dismissable per browser session (sessionStorage) so it does not nag on
 * every navigation but reappears on a fresh session.
 */
const DEMO_BANNER_DISMISS_KEY = "metalui:demoBannerDismissed";

function DemoModeBanner() {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DEMO_BANNER_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  return (
    <div
      role="status"
      data-testid="demo-mode-banner"
      className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
    >
      <FlaskConical className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1 text-sm leading-snug">
        <span className="font-semibold">Demo Mode</span> — this is a demo
        workspace. No real emails, messages, marketplace updates, postage
        labels, or payments are sent. Integrations show as{" "}
        <span className="font-medium">"Connected (Demo)"</span> and all external
        actions are safely simulated.
      </div>
      <button
        type="button"
        aria-label="Dismiss demo mode banner"
        data-testid="demo-mode-banner-dismiss"
        className="flex-shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-500/20"
        onClick={() => {
          try {
            sessionStorage.setItem(DEMO_BANNER_DISMISS_KEY, "1");
          } catch {
            /* sessionStorage unavailable — dismiss for this mount only */
          }
          setDismissed(true);
        }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
